import { listUploadImg } from '@src/constants/screen/util';
import { useDeleteImagesById, useImagesEventsById } from '@src/services/queries/useImagesQuery';
import { useUploadQuery } from '@src/services/queries/useUploadQuery';
import { useQueryClient } from '@tanstack/react-query';
import type { UploadProps } from 'antd';
import { message, Upload as Uploaded } from 'antd';
import { useTranslation } from 'react-i18next';
import ImageUpload from '@src/pages/Register/Upload/imageUpload';
import { Box } from '@mui/material';

interface UploadWidgetProps {
    eventId: number;
    onNext?: () => void;
    showNextButton?: boolean;
    title?: string;
    subtitle?: string;
    className?: string;
    style?: React.CSSProperties;
}

export default function UploadWidget({
    eventId,
    onNext,
    showNextButton = true,
    title,
    subtitle,
    className = "box",
    style = { marginBlockStart: '80px', borderRadius: '90px' }
}: UploadWidgetProps) {
    const queryClient = useQueryClient();
    const imagesEventsQuery = useImagesEventsById(eventId);
    const uploadQuery = useUploadQuery();
    const deleteImagesQuery = useDeleteImagesById();
    const { Dragger } = Uploaded;
    const { t } = useTranslation();

    const handleFileUpload = async (options: any) => {
        try {
            const { file } = options;
            const formData = new FormData();
            formData.append('name', file.name);
            formData.append('eventId', eventId.toString());
            formData.append('files', file);
            const data = await uploadQuery.mutateAsync(formData);
            if (data.status === 201) {
                message.success(`${file.name} uploaded successfully.`);
                queryClient.invalidateQueries({ queryKey: ['ImagesEventById', eventId] });
            } else {
                message.error(`${file.name} upload failed.`);
            }
        } catch (error) {
            console.error('Upload failed:', error);
        }
    };

    const handleRemoveImageById = async (imageId: number) => {
        try {
            const data = await deleteImagesQuery.mutateAsync(imageId);
            if (data.status === 200) {
                message.success('Remove image successfully!');
                queryClient.invalidateQueries({ queryKey: ['ImagesEventById', eventId] });
            } else {
                message.error('Remove image failed!');
            }
        } catch (error) {
            console.error(error);
        }
    };

    const props: UploadProps = {
        name: 'files',
        multiple: true,
        customRequest: handleFileUpload,
    };

    return (
        <div className={className} style={style}>
            {/* Desktop Layout */}
            <div className="columns is-mobile is-hidden-mobile is-hidden-tablet-only">
                <div className="column is-5 is-offset-1" style={{ marginBlockStart: '20px' }}>
                    <i className="fas fa-image" style={{ fontSize: '50px' }}></i>
                    <br />
                    <h1 className="content-header font-bold">{title || t('UPLOAD_PAGE.HEADER')}</h1>
                    <p className="">
                        <span className="has-text-danger">*</span>&nbsp;{subtitle || t('UPLOAD_PAGE.TITLE')}
                    </p>
                    <br />
                    <div className="column">
                        <Dragger {...props} accept={listUploadImg} showUploadList={false} style={{ borderRadius: '25px' }}>
                            <p className="ant-upload-drag-icon mt-24">
                                <i className="fas fa-upload" style={{ fontSize: '50px' }}></i>
                            </p>
                            <span className="file-label content-center" style={{ height: '180px', fontSize: '18px' }}>
                                {t('UPLOAD_PAGE.FILE_TYPE')}
                            </span>
                        </Dragger>
                    </div>
                    <br />
                    <br />
                </div>
                <div className="column is-5" style={{ marginBlockStart: '20px' }}>
                    <div className="w-full max-w-md mx-auto p-4">
                        <h2 className="text-lg font-semibold mb-4">
                            <div className="content-sub-header content-left">{t('UPLOAD_PAGE.FILE_UPLOAD')}</div>
                        </h2>
                        <div className="grid grid-cols-3 gap-4 max-h-[40vh] h-[40vh] overflow-y-auto p-2">
                            {imagesEventsQuery &&
                                imagesEventsQuery?.data &&
                                imagesEventsQuery?.data?.length > 0 &&
                                imagesEventsQuery?.data.map((item: any) => (
                                    <div className="col" key={item.id}>
                                        <ImageUpload url={item.url} onRemove={() => handleRemoveImageById(item.id)} />
                                    </div>
                                ))}
                        </div>
                        {showNextButton && onNext && (
                            <button
                                onClick={onNext}
                                className="button is-large is-fullwidth App"
                                style={{ backgroundColor: '#B5D224', fontSize: '18px', height: '70px' }}
                                disabled={imagesEventsQuery?.data?.length === 0}
                            >
                                {t('BUTTONS.NEXT')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile & Tablet Layout */}
            <div className="is-hidden-desktop">
                <div className="column" style={{ marginBlockStart: '20px' }}>
                    <div className="has-text-centered mb-4">
                        <i className="fas fa-image" style={{ fontSize: '40px' }}></i>
                        <br />
                        <h1 className="content-header font-bold" style={{ fontSize: '24px' }}>{title || t('UPLOAD_PAGE.HEADER')}</h1>
                        <p className="">
                            <span className="has-text-danger">*</span>&nbsp;{subtitle || t('UPLOAD_PAGE.TITLE')}
                        </p>
                    </div>

                    <div className="mb-4">
                        <Dragger {...props} accept={listUploadImg} showUploadList={false} style={{ borderRadius: '15px', minHeight: '120px' }}>
                            <p className="ant-upload-drag-icon mt-4">
                                <i className="fas fa-upload" style={{ fontSize: '30px' }}></i>
                            </p>
                            <span className="file-label content-center" style={{ height: '80px', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {t('UPLOAD_PAGE.FILE_TYPE')}
                            </span>
                        </Dragger>
                    </div>

                    <div className="w-full mx-auto p-2">
                        <h2 className="text-base font-semibold mb-3">
                            <div className="content-sub-header content-left">{t('UPLOAD_PAGE.FILE_UPLOAD')}</div>
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[35vh] h-[35vh] overflow-y-auto p-2 border rounded-lg">
                            {imagesEventsQuery &&
                                imagesEventsQuery?.data &&
                                imagesEventsQuery?.data?.length > 0 &&
                                imagesEventsQuery?.data.map((item: any) => (
                                    <div className="col" key={item.id}>
                                        <ImageUpload url={item.url} onRemove={() => handleRemoveImageById(item.id)} />
                                    </div>
                                ))}
                            {(!imagesEventsQuery?.data || imagesEventsQuery?.data?.length === 0) && (
                                <div className="col-span-full flex items-center justify-center h-32 text-gray-500">
                                    No images uploaded yet
                                </div>
                            )}
                        </div>
                        {showNextButton && onNext && (
                            <Box sx={{
                                mb: {
                                    xs: 10
                                }
                            }}>
                                <button
                                    onClick={onNext}
                                    className="button is-large is-fullwidth App mt-4"
                                    style={{ backgroundColor: '#B5D224', fontSize: '16px', height: '60px' }}
                                    disabled={imagesEventsQuery?.data?.length === 0}
                                >
                                    {t('BUTTONS.NEXT')}
                                </button>
                            </Box>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}